import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Link as RouterLink } from "react-router-dom";
import { ACCENT, TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import TutorsWorkspaceShell from "./TutorsWorkspaceShell";
import useTutorHub from "./useTutorHub";
import {
    TUTOR_DETAILS_KEYS,
    groupsAfterDelete,
    movePuzzleId,
    nestedGroupsForTree,
    newTutorId,
    orderedGroupsForTree,
    readTutorGroups,
    readTutorLibrary,
    readTutorLibraryAll,
} from "./tutorHubDetails";

function puzzleMatchesSearch(item, query) {
    const needle = String(query || "").trim().toLowerCase();
    if (!needle) {
        return true;
    }
    const title = String(item?.title || "").toLowerCase();
    const context = String(item?.sourceText || "").toLowerCase();
    return title.includes(needle) || context.includes(needle);
}

function contextSnippet(sourceText, query) {
    const text = String(sourceText || "").replace(/\s+/g, " ").trim();
    if (!text) {
        return "";
    }
    const needle = String(query || "").trim();
    if (!needle) {
        return text.length > 120 ? `${text.slice(0, 117)}…` : text;
    }
    const lower = text.toLowerCase();
    const at = lower.indexOf(needle.toLowerCase());
    if (at < 0) {
        return text.length > 120 ? `${text.slice(0, 117)}…` : text;
    }
    const start = Math.max(0, at - 28);
    const end = Math.min(text.length, at + needle.length + 72);
    const slice = `${start > 0 ? "…" : ""}${text.slice(start, end)}${end < text.length ? "…" : ""}`;
    return slice;
}

function GroupTreeNodeLabel({
    group,
    selected,
    editing,
    editName,
    saving,
    puzzleCountLabel,
    onSelect,
    onStartRename,
    onEditNameChange,
    onCommitRename,
    onCancelRename,
    onAddChild,
    onDelete,
    labels,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
                minHeight: 44,
                py: 0.25,
                pr: 0.5,
            }}
            onClick={(event) => {
                event.stopPropagation();
                if (!editing) {
                    onSelect(group.id);
                }
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {editing ? (
                    <TextField
                        autoFocus
                        size="small"
                        fullWidth
                        value={editName}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => onEditNameChange(event.target.value)}
                        onBlur={() => onCommitRename(group.id)}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                            if (event.key === "Enter") {
                                event.preventDefault();
                                onCommitRename(group.id);
                            }
                            if (event.key === "Escape") {
                                event.preventDefault();
                                onCancelRename();
                            }
                        }}
                        slotProps={{ htmlInput: { maxLength: 120 } }}
                        sx={{
                            "& .MuiInputBase-root": { fontWeight: 600 },
                        }}
                    />
                ) : (
                    <>
                        <Typography
                            noWrap
                            sx={{ fontWeight: selected ? 700 : 600, lineHeight: 1.3 }}
                        >
                            {group.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {puzzleCountLabel}
                        </Typography>
                    </>
                )}
            </Box>
            {!editing ? (
                <Box
                    sx={{ display: "flex", alignItems: "center", flexShrink: 0, ml: 0.5 }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Tooltip title={labels.rename}>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={labels.rename}
                                disabled={saving}
                                onClick={() => onStartRename(group)}
                                sx={{ p: 0.25 }}
                            >
                                <CreateOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={labels.addChild}>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={labels.addChild}
                                disabled={saving}
                                onClick={() => onAddChild(group.id)}
                                sx={{ p: 0.25 }}
                            >
                                <AddIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={labels.remove}>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={labels.remove}
                                disabled={saving}
                                onClick={() => onDelete(group.id)}
                                sx={{ p: 0.25 }}
                            >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ) : null}
        </Box>
    );
}

export default function TutorsDashboardPage({ embedded = false }) {
    const { t } = usePagesTranslation();
    const { details, loading, saving, error, patchDetails } = useTutorHub();
    const [selectedId, setSelectedId] = useState("");
    const [search, setSearch] = useState("");
    const [editingId, setEditingId] = useState("");
    const [editName, setEditName] = useState("");
    const [expandedItems, setExpandedItems] = useState([]);
    const groups = useMemo(() => readTutorGroups(details), [details]);
    const library = useMemo(() => readTutorLibrary(details), [details]);
    const deletedIds = useMemo(
        () => new Set(
            readTutorLibraryAll(details)
                .filter((item) => item.status === "DELETED")
                .map((item) => item.id),
        ),
        [details],
    );
    const nestedTree = useMemo(() => nestedGroupsForTree(groups), [groups]);
    const flatTree = useMemo(() => orderedGroupsForTree(groups), [groups]);

    useEffect(() => {
        if (!groups.length) {
            setSelectedId("");
            return;
        }
        if (!groups.some((group) => group.id === selectedId)) {
            setSelectedId(groups[0].id);
        }
    }, [groups, selectedId]);

    useEffect(() => {
        const ids = flatTree.map((group) => group.id);
        setExpandedItems((prev) => {
            const keep = prev.filter((id) => ids.includes(id));
            return keep.length ? keep : ids;
        });
    }, [flatTree]);

    const selectedGroup = groups.find((group) => group.id === selectedId) || null;
    const libraryById = useMemo(
        () => new Map(library.map((item) => [item.id, item])),
        [library],
    );
    const assignedPuzzles = useMemo(() => {
        if (!selectedGroup) {
            return [];
        }
        return selectedGroup.puzzleIds
            .filter((id) => !deletedIds.has(String(id)))
            .map((id) => libraryById.get(id) || {
            id,
            title: "",
            sourceText: "",
            missing: true,
        });
    }, [deletedIds, libraryById, selectedGroup]);
    const assignedIds = useMemo(
        () => new Set(selectedGroup?.puzzleIds || []),
        [selectedGroup],
    );
    const filteredPuzzles = useMemo(
        () => library.filter((item) => !assignedIds.has(item.id) && puzzleMatchesSearch(item, search)),
        [assignedIds, library, search],
    );

    const treeActionLabels = useMemo(() => ({
        rename: t("tutors.group-rename", "Rename"),
        addChild: t("tutors.group-add-child", "Add child"),
        remove: t("tutors.group-remove", "Remove"),
    }), [t]);

    const persistGroups = async (nextGroups) => {
        await patchDetails({ [TUTOR_DETAILS_KEYS.groups]: nextGroups });
    };

    const createGroup = async (parentId) => {
        const id = newTutorId();
        const nextParent = parentId && groups.some((group) => group.id === parentId) ? parentId : null;
        const defaultName = nextParent
            ? t("tutors.group-new-child-name", "New subgroup")
            : t("tutors.group-new-name", "New group");
        await persistGroups([
            ...groups,
            {
                id,
                name: defaultName,
                parentId: nextParent,
                puzzleIds: [],
                createdAt: new Date().toISOString(),
            },
        ]);
        if (nextParent) {
            setExpandedItems((prev) => (prev.includes(nextParent) ? prev : [...prev, nextParent]));
        }
        setSelectedId(id);
        setEditingId(id);
        setEditName(defaultName);
    };

    const handleStartRename = (group) => {
        setSelectedId(group.id);
        setEditingId(group.id);
        setEditName(group.name);
    };

    const handleCommitRename = async (groupId) => {
        if (editingId !== groupId) {
            return;
        }
        const trimmed = editName.trim();
        const current = groups.find((group) => group.id === groupId);
        setEditingId("");
        if (!trimmed || !current || trimmed === current.name) {
            setEditName("");
            return;
        }
        await persistGroups(groups.map((group) => (
            group.id === groupId ? { ...group, name: trimmed } : group
        )));
        setEditName("");
    };

    const handleCancelRename = () => {
        setEditingId("");
        setEditName("");
    };

    const handleDelete = async (groupId) => {
        if (editingId === groupId) {
            handleCancelRename();
        }
        await persistGroups(groupsAfterDelete(groups, groupId));
    };

    const handleTogglePuzzle = async (puzzleId, checked) => {
        if (!selectedGroup) {
            return;
        }
        await persistGroups(groups.map((group) => {
            if (group.id !== selectedGroup.id) {
                return group;
            }
            const puzzleIds = checked
                ? Array.from(new Set([...group.puzzleIds, puzzleId]))
                : group.puzzleIds.filter((id) => id !== puzzleId);
            return { ...group, puzzleIds };
        }));
    };

    const handleMovePuzzle = async (puzzleId, direction) => {
        if (!selectedGroup) {
            return;
        }
        await persistGroups(groups.map((group) => (
            group.id === selectedGroup.id
                ? { ...group, puzzleIds: movePuzzleId(group.puzzleIds, puzzleId, direction) }
                : group
        )));
    };

    const renderTreeItems = (nodes) => nodes.map((group) => (
        <TreeItem
            key={group.id}
            itemId={group.id}
            label={(
                <GroupTreeNodeLabel
                    group={group}
                    selected={group.id === selectedId}
                    editing={editingId === group.id}
                    editName={editName}
                    saving={saving}
                    puzzleCountLabel={t("tutors.group-puzzle-count", "{{count}} puzzles").replace(
                        "{{count}}",
                        String(group.puzzleIds.length),
                    )}
                    labels={treeActionLabels}
                    onSelect={setSelectedId}
                    onStartRename={handleStartRename}
                    onEditNameChange={setEditName}
                    onCommitRename={handleCommitRename}
                    onCancelRename={handleCancelRename}
                    onAddChild={createGroup}
                    onDelete={handleDelete}
                />
            )}
        >
            {group.children?.length ? renderTreeItems(group.children) : null}
        </TreeItem>
    ));

    if (embedded && loading) {
        return (
            <Box sx={{ mt: { xs: 5, sm: 8 } }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: TEXT.primary, mb: 2 }}>
                    {t("tutors.groups-title", "Group")}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    const content = (
        <>
            {embedded ? null : (
                <Typography variant="body1" sx={{ color: TEXT.secondary, lineHeight: 1.7, mb: 3 }}>
                    {t(
                        "tutors.groups-body",
                        "Manage the group tree on the left. Use rename, add child, or remove on each node. Assign private crosswords on the right, then move them up or down to set the order.",
                    )}
                </Typography>
            )}

            {embedded && groups.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={saving}
                        onClick={() => createGroup(null)}
                        sx={{ minHeight: 44, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                    >
                        {t("tutors.group-create", "Add group")}
                    </Button>
                </Box>
            ) : (
            <Grid container spacing={2} alignItems="stretch">
                <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
                    <Card sx={{ p: 2, flex: 1, width: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                                {t("tutors.groups-list-title", "Groups")}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                disabled={saving}
                                onClick={() => createGroup(null)}
                                sx={{ minHeight: 44, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                            >
                                {t("tutors.group-create", "Add group")}
                            </Button>
                        </Box>
                        {groups.length === 0 ? (
                            <Typography color="text.secondary">
                                {t("tutors.groups-empty", "No groups yet. Add a group, then assign puzzles on the right.")}
                            </Typography>
                        ) : (
                            <Box sx={{ maxHeight: { md: "min(60vh, 640px)" }, overflowY: "auto" }}>
                                <SimpleTreeView
                                    aria-label={t("tutors.groups-list-title", "Groups")}
                                    expandedItems={expandedItems}
                                    onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
                                    selectedItems={selectedId || null}
                                    onSelectedItemsChange={(_event, itemId) => {
                                        if (itemId) {
                                            setSelectedId(itemId);
                                        }
                                    }}
                                    sx={{
                                        "& .MuiTreeItem-content": {
                                            borderRadius: 1,
                                            mb: 0.25,
                                            py: 0.25,
                                        },
                                        "& .MuiTreeItem-label": {
                                            width: "100%",
                                            paddingLeft: 0.5,
                                        },
                                    }}
                                >
                                    {renderTreeItems(nestedTree)}
                                </SimpleTreeView>
                            </Box>
                        )}
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 7 }} sx={{ display: "flex" }}>
                    <Card sx={{ p: 2, flex: 1, width: "100%" }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                            {t("tutors.group-puzzles-title", "Puzzles")}
                        </Typography>
                        {!selectedGroup ? (
                            <Typography color="text.secondary">
                                {t("tutors.group-select-first", "Select or create a group to assign puzzles.")}
                            </Typography>
                        ) : library.length === 0 && assignedPuzzles.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {t("tutors.group-no-puzzles", "Add a crossword in Story library first.")}{" "}
                                <Button component={RouterLink} to="/tutors/library" size="small" sx={{ textTransform: "none" }}>
                                    {t("nav.tutors-library", "Story library")}
                                </Button>
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {t("tutors.group-order-title", "Order in this group")}
                                </Typography>
                                <Typography variant="body2" sx={{ color: TEXT.secondary, mb: 1 }}>
                                    {t(
                                        "tutors.group-order-hint",
                                        "Move puzzles up or down. This order is saved for {{name}}.",
                                    ).replace("{{name}}", selectedGroup.name)}
                                </Typography>
                                {assignedPuzzles.length === 0 ? (
                                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                                        {t(
                                            "tutors.group-order-empty",
                                            "No puzzles in this group yet. Add some below, then move them up or down.",
                                        )}
                                    </Typography>
                                ) : (
                                    <Box sx={{ maxHeight: { md: "min(32vh, 360px)" }, overflowY: "auto", mb: 2 }}>
                                        {assignedPuzzles.map((item, index) => {
                                            const snippet = item.missing ? "" : contextSnippet(item.sourceText, "");
                                            const title = item.missing
                                                ? t("tutors.group-missing-puzzle", "Removed from library")
                                                : item.title;
                                            return (
                                                <Box
                                                    key={item.id}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: 0.5,
                                                        py: 0.75,
                                                        borderBottom: 1,
                                                        borderColor: "divider",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ width: 28, pt: 1.25, color: TEXT.secondary, fontWeight: 700 }}
                                                    >
                                                        {index + 1}
                                                    </Typography>
                                                    <Box sx={{ flex: 1, minWidth: 0, pt: 0.75 }}>
                                                        <Typography sx={{ fontWeight: 600 }} noWrap>
                                                            {title}
                                                        </Typography>
                                                        {snippet ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {snippet}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                    <IconButton
                                                        aria-label={t("tutors.group-move-up", "Move up")}
                                                        onClick={() => handleMovePuzzle(item.id, -1)}
                                                        disabled={saving || index === 0}
                                                        sx={{ minWidth: 44, minHeight: 44 }}
                                                    >
                                                        <ArrowUpwardIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label={t("tutors.group-move-down", "Move down")}
                                                        onClick={() => handleMovePuzzle(item.id, 1)}
                                                        disabled={saving || index === assignedPuzzles.length - 1}
                                                        sx={{ minWidth: 44, minHeight: 44 }}
                                                    >
                                                        <ArrowDownwardIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label={t("tutors.group-remove-puzzle", "Remove from group")}
                                                        onClick={() => handleTogglePuzzle(item.id, false)}
                                                        disabled={saving}
                                                        sx={{ minWidth: 44, minHeight: 44 }}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}

                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    {t("tutors.group-add-title", "Add puzzles")}
                                </Typography>
                                {library.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t("tutors.group-no-puzzles", "Add a crossword in Story library first.")}{" "}
                                        <Button component={RouterLink} to="/tutors/library" size="small" sx={{ textTransform: "none" }}>
                                            {t("nav.tutors-library", "Story library")}
                                        </Button>
                                    </Typography>
                                ) : (
                                    <>
                                        <TextField
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder={t("tutors.group-puzzle-search", "Search title or context")}
                                            fullWidth
                                            size="small"
                                            sx={{ mb: 1.5 }}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ color: TEXT.secondary, mb: 1 }}>
                                            {t(
                                                "tutors.group-attach-hint",
                                                "Check a puzzle to add it at the end of {{name}}.",
                                            ).replace("{{name}}", selectedGroup.name)}
                                        </Typography>
                                        {filteredPuzzles.length === 0 ? (
                                            <Typography color="text.secondary">
                                                {assignedIds.size > 0 && !search.trim()
                                                    ? t(
                                                        "tutors.group-add-empty",
                                                        "Every library puzzle is already in this group.",
                                                    )
                                                    : t("tutors.group-search-empty", "No puzzles match that search.")}
                                            </Typography>
                                        ) : (
                                            <Box sx={{ maxHeight: { md: "min(32vh, 360px)" }, overflowY: "auto" }}>
                                                {filteredPuzzles.map((item) => {
                                                    const snippet = contextSnippet(item.sourceText, search);
                                                    return (
                                                        <Box
                                                            key={item.id}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "flex-start",
                                                                gap: 0.5,
                                                                py: 0.75,
                                                                borderBottom: 1,
                                                                borderColor: "divider",
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                sx={{ alignItems: "flex-start", m: 0, flex: 1, minWidth: 0 }}
                                                                control={
                                                                    <Checkbox
                                                                        checked={false}
                                                                        onChange={() => handleTogglePuzzle(item.id, true)}
                                                                        disabled={saving}
                                                                        sx={{ mt: -0.5 }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box sx={{ minWidth: 0 }}>
                                                                        <Typography sx={{ fontWeight: 600 }}>
                                                                            {item.title}
                                                                        </Typography>
                                                                        {snippet ? (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                                                                {snippet}
                                                                            </Typography>
                                                                        ) : null}
                                                                    </Box>
                                                                }
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Card>
                </Grid>
            </Grid>
            )}
        </>
    );

    if (embedded) {
        return (
            <Box sx={{ mt: { xs: 5, sm: 8 } }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: TEXT.primary, mb: 2 }}>
                    {t("tutors.groups-title", "Group")}
                </Typography>
                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}
                {content}
            </Box>
        );
    }

    return (
        <TutorsWorkspaceShell
            title={t("tutors.groups-title", "Group")}
            maxWidth={1200}
            loading={loading}
            error={error}
        >
            {content}
        </TutorsWorkspaceShell>
    );
}
